import { useState } from "react";
import { useTranslation } from "react-i18next";
import { XLogo, TelegramLogo, WhatsappLogo, FacebookLogo, LinkedinLogo, RedditLogo, LinkSimple, Check } from "@phosphor-icons/react";

const ShareButtons = ({ title, url }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);

  const targets = [
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`,
      Icon: XLogo,
      color: "text-white hover:bg-white/20 hover:border-white/60",
      ring: "bg-white/10 border-white/30",
      testid: "share-x",
    },
    {
      name: "Telegram",
      href: `https://t.me/share/url?url=${shareUrl}&text=${shareTitle}`,
      Icon: TelegramLogo,
      color: "text-[#229ED9] hover:bg-[#229ED9]/25 hover:border-[#229ED9]/60",
      ring: "bg-[#229ED9]/10 border-[#229ED9]/30",
      testid: "share-telegram",
    },
    {
      name: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${shareTitle}%20${shareUrl}`,
      Icon: WhatsappLogo,
      color: "text-[#25D366] hover:bg-[#25D366]/25 hover:border-[#25D366]/60",
      ring: "bg-[#25D366]/10 border-[#25D366]/30",
      testid: "share-whatsapp",
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      Icon: FacebookLogo,
      color: "text-[#1877F2] hover:bg-[#1877F2]/25 hover:border-[#1877F2]/60",
      ring: "bg-[#1877F2]/10 border-[#1877F2]/30",
      testid: "share-facebook",
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      Icon: LinkedinLogo,
      color: "text-[#0A66C2] hover:bg-[#0A66C2]/25 hover:border-[#0A66C2]/60",
      ring: "bg-[#0A66C2]/10 border-[#0A66C2]/30",
      testid: "share-linkedin",
    },
    {
      name: "Reddit",
      href: `https://reddit.com/submit?url=${shareUrl}&title=${shareTitle}`,
      Icon: RedditLogo,
      color: "text-[#FF4500] hover:bg-[#FF4500]/25 hover:border-[#FF4500]/60",
      ring: "bg-[#FF4500]/10 border-[#FF4500]/30",
      testid: "share-reddit",
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-white/10" data-testid="share-buttons">
      <p className="text-sm font-medium text-gray-300 mb-4">{t("blog.share.title")}</p>
      <div className="flex flex-wrap items-center gap-3">
        {targets.map(({ name, href, Icon, color, ring, testid }) => (
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t("blog.share.title")} - ${name}`}
            data-testid={testid}
            className={`w-10 h-10 rounded-full flex items-center justify-center border ${ring} ${color} transition-all`}
          >
            <Icon size={20} weight="fill" />
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          data-testid="share-copy-link"
          aria-label={t("blog.share.copy")}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-white/20 bg-white/5 text-sm text-gray-200 hover:bg-white/10 hover:border-white/40 transition-all"
        >
          {copied ? <Check size={16} weight="bold" /> : <LinkSimple size={16} weight="bold" />}
          <span>{copied ? t("blog.share.copied") : t("blog.share.copy")}</span>
        </button>
      </div>
    </div>
  );
};

export default ShareButtons;
